import React, { Suspense, type ReactNode } from 'react';

interface SuspenseWrapperProps {
  children: ReactNode;
  fallbackClassName?: string;
}

/**
 * A reusable Suspense wrapper to minimize Cumulative Layout Shift (CLS).
 * It renders a pulsing skeleton with the dimensions provided via fallbackClassName.
 */
const SuspenseWrapper: React.FC<SuspenseWrapperProps> = ({
  children,
  fallbackClassName = 'w-full h-32',
}) => {
  return (
    <Suspense
      fallback={
        <div
          className={`animate-pulse bg-primary-light rounded-xl ${fallbackClassName}`}
          aria-hidden="true"
        />
      }
    >
      {children}
    </Suspense>
  );
};

export default SuspenseWrapper;

/**
 * Usage Examples:
 *
 * 1. 16:9 Image/Video Card:
 * <SuspenseWrapper fallbackClassName="aspect-video w-full">
 *   <VideoComponent />
 * </SuspenseWrapper>
 *
 * 2. Standard Text Paragraph (fixed min height):
 * <SuspenseWrapper fallbackClassName="h-[100px] w-full">
 *   <LongParagraphComponent />
 * </SuspenseWrapper>
 *
 * 3. User Avatar (Circular):
 * <SuspenseWrapper fallbackClassName="w-12 h-12 rounded-full">
 *   <AvatarComponent />
 * </SuspenseWrapper>
 */
